// Finance — KPI strip backed by /admin/finance.

import { useQuery } from '@tanstack/react-query';
import { KPI, PageHeader, ToolBtn } from '@drivecars/admin-ui';
import { fetchAdminFinance, downloadFinanceCsv } from '@/lib/admin-api';

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(v);

export function Finance() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'finance'],
    queryFn: ({ signal }) => fetchAdminFinance(signal),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Finance"
        title="Finance"
        sub="Marketplace cashflow at a glance."
        actions={
          <ToolBtn icon="download" onClick={() => void downloadFinanceCsv()}>
            Export
          </ToolBtn>
        }
      />
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load finance: {(error as Error).message}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <KPI
          label="GMV · 30d"
          value={isLoading ? '…' : fmtMoney(data?.gmv30d ?? 0)}
          hint="confirmed + completed"
        />
        <KPI
          label="Commission · 30d"
          value={isLoading ? '…' : fmtMoney(data?.commission30d ?? 0)}
          hint="net take"
        />
        <KPI
          label="Payouts pending"
          value={isLoading ? '…' : fmtMoney(data?.payoutsPending ?? 0)}
        />
        <KPI
          label="Payouts released"
          value={isLoading ? '…' : fmtMoney(data?.payoutsReleased ?? 0)}
        />
        <KPI
          label="Wallet balance"
          value={isLoading ? '…' : fmtMoney(data?.walletBalance ?? 0)}
        />
      </div>
    </div>
  );
}
